import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Badge, Button, ChoiceCard, ChoiceGroup, CodeField, Combobox, DataTable, DefinitionList, Dialog, EmptyState, ErrorSummary,
  FormSection, Inline, Link, LiveSummary, Select, Spinner, Stack, t, Tabs, Text, TextField, toast, WizardLayout,
} from '@kanbios/ui';
import type { github, maintenance } from '@kanbios/api-client';
import { RefusalPage } from '../../components/Refusal';
import { QualificationDraft, QualificationFields } from '../../components/catalogue/QualificationFields';
import { api, ApiFailure, causeOf, unwrap } from '../../lib/api';
import { relativeTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import { PendingButton } from '../../components/PendingButton';
import { openPullRequestOf, PullRequestConflict } from '../../components/PullRequestConflict';
import { asName } from '../../lib/environments';
import { FULL_HEIGHT } from '../../lib/tables';
import { changeRef } from '../../components/catalogue/PullRequestNotice';

type Repository = github.components['schemas']['Repository'];
type PullRequest = maintenance.components['schemas']['PullRequest'];
type Created = { project: string; pullRequests: PullRequest[]; failed: { repository: string; message: string }[] };
/** D48: an environment of the project, a free name and a type. */
type EnvironmentRow = { key: number; name: string; type: string | null };

const STEPS = ['client', 'project', 'applications', 'environments', 'verification'] as const;
const NEW_CLIENT = '__new__';
const PR_TONES = { open: 'info', validation_failed: 'neg', merged: 'info', applied: 'pos', closed: 'neutral' } as const;
const EMPTY_QUALIFICATION: QualificationDraft = { criticality: null, healthData: null, dora: null, sensitivePersonalData: null, internetExposed: null };

/** A first guess of the role from the repository's name; the person confirms it. */
const guessRoles = (name: string): string[] => {
  const role = /admin/.test(name) ? 'admin-frontend' : /^(api|back)|-api$/.test(name) ? 'backend' : /^front|web/.test(name) ? 'frontend' : /worker/.test(name) ? 'worker' : /cron|batch/.test(name) ? 'cron' : null;
  return role ? [role] : [];
};

/**
 * Assistant « Nouveau projet » (SPEC-006), DSM Wizard page type: Client,
 * Projet, Applications, Environnements, Vérification, then the pull requests
 * opened. Every rule is checked by the backend (`POST /projects/preview`).
 */
export function NewProjectPage() {
  const navigate = useNavigate();
  const { enums } = useSession();
  const [step, setStep] = useState(0);
  const [tried, setTried] = useState<Record<number, boolean>>({});
  const [client, setClient] = useState<string | null>(null);
  const [newClient, setNewClient] = useState({ title: '', contactEmail: '' });
  const [title, setTitle] = useState('');
  const [team, setTeam] = useState<string | null>(null);
  const [qualification, setQualification] = useState<QualificationDraft>(EMPTY_QUALIFICATION);
  const [picked, setPicked] = useState<Record<string, true>>({});
  const [roles, setRoles] = useState<Record<string, string[]>>({});
  // D48: the usual production first; the person names, types, adds or removes the others.
  const [environments, setEnvironments] = useState<EnvironmentRow[]>([{ key: 1, name: 'prod', type: 'production' }]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('catalogue');
  const [quit, setQuit] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [sending, setSending] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);

  const options = useQuery({
    queryKey: ['new-project-options'],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/new/options')),
    retry: false,
  });
  const repositories = useQuery({
    queryKey: ['github-repositories', search],
    queryFn: async () => unwrap(await api.github.GET('/repositories', { params: { query: { q: search || undefined } } })),
    placeholderData: keepPreviousData,
    enabled: options.isSuccess,
  });
  const chosenRepositories = Object.keys(picked).sort();
  const body = useMemo(() => ({
    client: client === NEW_CLIENT ? { new: newClient } : client ? { name: client } : undefined,
    project: {
      title, team: team ?? undefined, criticality: qualification.criticality ?? undefined,
      qualification: { healthData: qualification.healthData, dora: qualification.dora, sensitivePersonalData: qualification.sensitivePersonalData, internetExposed: qualification.internetExposed },
    },
    applications: chosenRepositories.map(repository => ({ repository, roles: roles[repository] ?? [] })),
    environments: environments.map(e => ({ name: e.name.trim(), type: e.type ?? undefined })),
  }), [client, newClient, title, team, qualification, chosenRepositories.join(), roles, environments]);
  const preview = useQuery({
    queryKey: ['new-project-preview', body],
    queryFn: async () => unwrap(await api.maintenance.POST('/projects/preview', { body })),
    placeholderData: keepPreviousData,
    enabled: options.isSuccess,
  });
  // RG-006-13: the changes committed are read again until applied.
  const states = useQueries({
    queries: (created?.pullRequests ?? []).map(pr => ({
      queryKey: ['catalogue-change', pr.id],
      queryFn: async () => unwrap(await api.maintenance.GET('/catalogue-changes/{id}', { params: { path: { id: pr.id } } })),
      refetchInterval: (q: { state: { data?: PullRequest } }) => (q.state.data && ['applied', 'closed'].includes(q.state.data.state) ? false : 5000),
    })),
  });
  useEffect(() => { if (tab !== 'catalogue' && !chosenRepositories.includes(tab)) setTab('catalogue'); }, [tab, chosenRepositories.join()]);

  // « Continuer » clicked while the draft is being checked: kept, and applied once the check is back without errors.
  const [advancing, setAdvancing] = useState(false);
  // Before any early return: the hooks of a component are the same at every render.
  useEffect(() => {
    if (!advancing || preview.isFetching) return;
    setAdvancing(false);
    if (!(preview.data?.errors ?? []).some(e => e.step === STEPS[step])) setStep(step + 1);
  }, [advancing, preview.isFetching]);
  if (options.error) return <RefusalPage error={options.error} />;
  const data = options.data;
  const errors = preview.data?.errors ?? [];
  const errorsOf = (s: number) => errors.filter(e => e.step === STEPS[s]);
  const fieldError = (field: string) => (tried[step] ? errors.find(e => e.field === field)?.message : undefined);
  const clientTitle = client === NEW_CLIENT ? newClient.title : data?.clients.find(c => c.name === client)?.title;
  const criticality = enums.criticalities.find(c => c.value === qualification.criticality)?.label;
  const obligationLabel = (o: string) => enums.obligations.find(x => x.value === o)?.label ?? o;
  const obligations = [qualification.healthData && 'hds', qualification.dora && 'dora', qualification.sensitivePersonalData && 'sensitive'].filter((o): o is string => Boolean(o)).map(obligationLabel).join(', ');
  const qualified = Object.values(qualification).every(v => v !== null);
  const typeLabel = (type: string | null) => enums.environmentTypes.find(x => x.value === type)?.label ?? '';
  const envLabel = (e: EnvironmentRow) => (e.type ? `${e.name} (${typeLabel(e.type)})` : e.name);
  const setEnvironment = (key: number, patch: Partial<EnvironmentRow>) => setEnvironments(list => list.map(e => (e.key === key ? { ...e, ...patch } : e)));
  const addEnvironment = () => setEnvironments(list => [...list, { key: Math.max(0, ...list.map(e => e.key)) + 1, name: '', type: null }]);
  const roleLabel = (role: string | null | undefined) => enums.applicationRoles.find(r => r.value === role)?.label ?? '';
  const filled = Boolean(client || newClient.title || title || team || chosenRepositories.length || environments.length !== 1 || environments[0]?.name !== 'prod' || Object.values(qualification).some(v => v !== null));
  const leave = () => navigate('/projects');
  // RG-006-3: confirmation as soon as a field is filled.
  const close = () => (filled && !created ? setQuit(true) : leave());
  const steps = STEPS.map(s => t(`newProject.steps.${s}`));
  const pullRequests = preview.data?.pullRequests ?? chosenRepositories.length + 1;

  const next = () => {
    setTried(x => ({ ...x, [step]: true }));
    if (preview.isFetching) { setAdvancing(true); return; }
    if (errorsOf(step).length) return;
    setStep(step + 1);
  };
  const submit = async () => {
    setSending(true);
    setFailure(null);
    try {
      const result = unwrap(await api.maintenance.POST('/projects', { body }));
      setCreated(result as Created);
      toast({ tone: 'pos', title: t('newProject.done.toast', { n: result.pullRequests.length - 1 }) });
    } catch (e) {
      const f = e instanceof ApiFailure ? e : null;
      setFailure(f);
      // 409: the project is already declared; back to the Projet step.
      if (f?.status === 409) { setStep(1); setTried(x => ({ ...x, 1: true })); }
    } finally {
      setSending(false);
    }
  };

  // --- After the last step: the pull requests opened (DSM Wizard, « et maintenant ? »).
  if (created) {
    const live = created.pullRequests.map((pr, i) => states[i]?.data ?? pr);
    const catalogueApplied = live[0]?.state === 'applied';
    return (
      <WizardLayout
        title={t('newProject.title')}
        steps={steps}
        current={4}
        onClose={leave}
        closeLabel={t('newProject.quit')}
        eyebrow={false}
        // The table of pull requests reads better on the full width than in the 720px of a form.
        maxWidth={1080}
        heading={t('newProject.done.heading', { n: created.pullRequests.length - 1 })}
        lead={t('newProject.done.lead')}
        footerStart={<Button variant="secondary" onClick={leave}>{t('newProject.done.list')}</Button>}
        footerEnd={(
          <Button trailingIcon="arrow_forward" disabled={!catalogueApplied} onClick={() => navigate(`/projects/${created.project}/servers`)}>
            {t('newProject.done.next')}
          </Button>
        )}
        // The page follows the pull requests every 5 s: say it is waiting (états transverses « Action longue »).
        footerText={catalogueApplied ? '' : <Inline gap={8} align="center"><Spinner size={16} />{t('newProject.done.waiting')}</Inline>}
      >
        <Stack gap={16}>
          {created.failed.length > 0 && (
            <Alert tone="warn" title={t('newProject.done.failedTitle', { n: created.failed.length })}>
              {created.failed.map(f => `${f.repository} : ${f.message}`).join(' ')}
            </Alert>
          )}
          <DataTable
            height={FULL_HEIGHT}
            label={t('newProject.done.table')}
            rows={live}
            rowKey={(r: PullRequest) => r.id}
            toolbar={false}
            columns={[
              { key: 'repository', label: t('newProject.done.repository'), mono: true, grow: true },
              // The link carries the number: no column of its own.
              { key: 'url', label: t('newProject.done.link'), width: 220, sortable: false, render: (r: PullRequest) => <Link href={r.url} external>{t('pullRequest.link', { ref: changeRef(r) })}</Link> },
              { key: 'state', label: t('newProject.done.state'), width: 200, render: (r: PullRequest) => <Badge tone={PR_TONES[r.state]}>{t(`newProject.prStates.${r.state}`)}</Badge> },
            ]}
          />
        </Stack>
      </WizardLayout>
    );
  }

  const summaryErrors = tried[step] ? errorsOf(step).map(e => ({ message: e.message, fieldId: e.field.replace(/[^a-zA-Z0-9]/g, '-') })) : [];
  const repositoryRows = repositories.data?.items ?? [];
  const recent = (data?.recent ?? []).map(name => data!.clients.find(c => c.name === name)!).filter(Boolean);

  return (
    <>
      <WizardLayout
        title={t('newProject.title')}
        subtitle={t('newProject.subtitle')}
        steps={steps}
        current={step}
        onStepSelect={i => { if (i < step) setStep(i); }}
        onClose={close}
        closeLabel={t('newProject.quit')}
        heading={t(`newProject.${STEPS[step]}.heading`)}
        lead={t(`newProject.${STEPS[step]}.lead`)}
        maxWidth={step === 2 || step === 4 ? 1240 : undefined}
        aside={
          <LiveSummary
            eyebrow={t('newProject.summary.eyebrow')}
            title={title || t('newProject.summary.titleEmpty')}
            rows={[
              { icon: 'business', value: clientTitle || null, placeholder: t('newProject.summary.clientTodo') },
              { icon: 'speed', value: criticality ?? null, placeholder: t('newProject.summary.criticalityTodo') },
              { icon: 'verified_user', value: qualified ? obligations || t('newProject.summary.noObligation') : null, placeholder: t('newProject.summary.qualificationTodo') },
              { icon: 'apps', value: chosenRepositories.length ? t('newProject.summary.applications', { n: chosenRepositories.reduce((n, r) => n + Math.max(1, roles[r]?.length ?? 0), 0) }) : null, placeholder: t('newProject.summary.applicationsTodo') },
              { icon: 'layers', value: environments.some(e => e.name) ? environments.filter(e => e.name).map(envLabel).join(', ') : null, placeholder: t('newProject.summary.environmentsTodo') },
              { icon: 'deployed_code', value: preview.data?.deployments ? t('newProject.summary.deployments', { n: preview.data.deployments }) : null, placeholder: t('newProject.summary.deploymentsTodo') },
            ]}
            footer={chosenRepositories.length ? t('newProject.summary.footer', { n: pullRequests - 1 }) : t('newProject.summary.footerTodo')}
            footerIcon="merge"
          />
        }
        footerStart={<Button variant="secondary" icon="arrow_back" disabled={step === 0} onClick={() => setStep(step - 1)}>{t('newProject.previous')}</Button>}
        footerEnd={step < 4
          ? <Button trailingIcon="arrow_forward" onClick={next}>{t('newProject.continue')}</Button>
          : <PendingButton icon="merge" pending={sending} disabled={errors.length > 0 || preview.isFetching} onClick={submit}>{t('newProject.open', { n: pullRequests - 1 })}</PendingButton>}
      >
        <Stack gap={24}>
          {summaryErrors.length > 0 && <ErrorSummary title={t('newProject.errorsTitle')} errors={summaryErrors} />}
          {failure && openPullRequestOf(failure) && <PullRequestConflict failure={failure} onClosed={() => setFailure(null)} />}
          {failure && !openPullRequestOf(failure) && (
            <Alert tone="neg" title={failure.body.message || t('newProject.githubDown')}>
              {failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}
            </Alert>
          )}

          {step === 0 && data && (
            <Stack gap={24}>
              <ChoiceGroup label={t('newProject.client.recent')}>
                {recent.map(c => (
                  <ChoiceCard key={c.name} variant="radio" title={c.title} description={t('newProject.client.projects', { n: c.projects })}
                    selected={client === c.name} onClick={() => setClient(c.name)} />
                ))}
                {data.canCreateClient && (
                  <ChoiceCard variant="radio" title={t('newProject.client.new')} description={t('newProject.client.newHint')}
                    selected={client === NEW_CLIENT} onClick={() => setClient(NEW_CLIENT)} />
                )}
              </ChoiceGroup>
              <Combobox
                id="client-name"
                label={t('newProject.client.search')}
                searchable
                placeholder={t('newProject.client.searchPlaceholder')}
                options={data.clients.map(c => ({ value: c.name, label: c.title }))}
                value={client && client !== NEW_CLIENT ? client : null}
                onChange={(v: string | null) => setClient(v)}
                error={fieldError('client.name') ?? fieldError('client')}
              />
              {client === NEW_CLIENT && (
                <FormSection layout="stacked" headingLevel={2} title={t('newProject.client.newTitle')}>
                  <TextField id="client-new-title" label={t('newProject.client.name')} value={newClient.title} required
                    onChange={(e: { target: { value: string } }) => setNewClient(c => ({ ...c, title: e.target.value }))} error={fieldError('client.new.title')} />
                  <TextField id="client-new-contactEmail" type="email" label={t('newProject.client.contact')} value={newClient.contactEmail} required hint={t('newProject.client.contactHint')}
                    onChange={(e: { target: { value: string } }) => setNewClient(c => ({ ...c, contactEmail: e.target.value }))} error={fieldError('client.new.contactEmail')} />
                </FormSection>
              )}
            </Stack>
          )}

          {step === 1 && data && (
            <Stack gap={24}>
              <FormSection layout="stacked" headingLevel={2} title={t('newProject.project.section')}>
                <TextField id="project-title" label={t('newProject.project.name')} value={title} required hint={preview.data?.project ? t('newProject.project.identifier', { name: preview.data.project }) : undefined}
                  onChange={(e: { target: { value: string } }) => setTitle(e.target.value)} error={fieldError('project.title')} />
                <Select id="project-team" label={t('newProject.project.team')} searchable required value={team} onChange={setTeam}
                  options={data.teams.map(x => ({ value: x, label: x }))} placeholder={t('newProject.project.teamPlaceholder')} error={fieldError('project.team')} />
              </FormSection>
              <FormSection layout="stacked" headingLevel={2} title={t('newProject.project.qualification')}>
                <QualificationFields value={qualification} onChange={setQualification} errors={Boolean(tried[1])} />
              </FormSection>
            </Stack>
          )}

          {step === 2 && (
            <DataTable
              label={t('newProject.applications.table')}
              rows={repositoryRows}
              rowKey="fullName"
              selectable
              selected={picked}
              onSelectionChange={sel => {
                const allowed = Object.keys(sel).filter(k => repositoryRows.some(r => r.fullName === k && !r.project) || picked[k]);
                setPicked(Object.fromEntries(allowed.map(k => [k, true as const])));
                setRoles(r => Object.fromEntries(allowed.map(k => [k, r[k] ?? guessRoles(k.split('/')[1] ?? '')])));
              }}
              manual
              searchPlaceholder={t('newProject.applications.search')}
              onQueryChange={q => setSearch(q.query)}
              toolbarFilters={false}
              toolbarColumns={false}
              loading={repositories.isPending}
              height={FULL_HEIGHT}
              rowState={(r: Repository) => (r.project ? 'dimmed' : null)}
              columns={[
                { key: 'name', label: t('newProject.applications.repository'), mono: true, width: 260 },
                { key: 'language', label: t('newProject.applications.language'), width: 140, render: (r: Repository) => r.language ?? '—' },
                { key: 'pushedAt', label: t('newProject.applications.pushedAt'), width: 170, render: (r: Repository) => (r.pushedAt ? relativeTime(r.pushedAt) : '—') },
                {
                  key: 'role', label: t('newProject.applications.role'), grow: true, editable: true, sortable: false,
                  render: (r: Repository) => r.project
                    ? <Text variant="body-sm" tone="mute" truncate>{t('newProject.applications.held', { project: r.project.title })}</Text>
                    : picked[r.fullName]
                      // Several roles: a monorepo, one application per role (sponsor decision of 17 October 2026).
                      ? <Combobox multiple size="sm" ariaLabel={t('newProject.applications.roleOf', { repository: r.name })} value={roles[r.fullName] ?? []}
                          onChange={(v: string[]) => setRoles(x => ({ ...x, [r.fullName]: v }))} options={enums.applicationRoles}
                          placeholder={t('newProject.applications.rolePlaceholder')} error={fieldError(`applications[${chosenRepositories.indexOf(r.fullName)}].role`) ? ' ' : undefined} />
                      : null,
                },
              ]}
              empty={<EmptyState framed={false} icon="folder_off" title={t('newProject.applications.empty')} />}
            />
          )}

          {step === 3 && (
            <Stack gap={16}>
              <DataTable
                height={FULL_HEIGHT}
                label={t('newProject.environments.label')}
                rows={environments.map((e, i) => ({ ...e, i }))}
                rowKey="key"
                toolbar={false}
                rowHeight="auto"
                columns={[
                  {
                    key: 'name', label: t('newProject.environments.name'), width: 300, editable: true, sortable: false,
                    render: (e: EnvironmentRow & { i: number }) => (
                      <TextField size="sm" ariaLabel={t('newProject.environments.nameOf', { n: e.i + 1 })} value={e.name} placeholder={t('newProject.environments.namePlaceholder')}
                        onChange={(x: { target: { value: string } }) => setEnvironment(e.key, { name: asName(x.target.value, 30) })} error={fieldError(`environments[${e.i}].name`)} />
                    ),
                  },
                  {
                    key: 'type', label: t('newProject.environments.type'), width: 260, editable: true, sortable: false,
                    render: (e: EnvironmentRow & { i: number }) => (
                      <Select size="sm" ariaLabel={t('newProject.environments.typeOf', { n: e.i + 1 })} value={e.type} placeholder={t('newProject.environments.typeTodo')}
                        options={enums.environmentTypes} onChange={(v: string | null) => setEnvironment(e.key, { type: v })} error={fieldError(`environments[${e.i}].type`)} />
                    ),
                  },
                  {
                    key: 'remove', label: '', grow: true, align: 'right', sortable: false,
                    render: (e: EnvironmentRow & { i: number }) => (
                      <Button variant="ghost" size="sm" icon="delete" aria-label={t('newProject.environments.removeOf', { n: e.i + 1 })}
                        onClick={() => setEnvironments(list => list.filter(x => x.key !== e.key))}>{t('newProject.environments.remove')}</Button>
                    ),
                  },
                ]}
                empty={<EmptyState framed={false} icon="layers" title={t('newProject.environments.empty')} />}
              />
              <Inline>
                <Button variant="secondary" size="sm" icon="add" onClick={addEnvironment}>{t('newProject.environments.add')}</Button>
              </Inline>
              {fieldError('environments') && <Text tone="neg">{fieldError('environments')}</Text>}
              {preview.data && preview.data.deployments > 0 && <Text>{t('newProject.environments.count', { n: preview.data.deployments })}</Text>}
            </Stack>
          )}

          {step === 4 && preview.data && (
            <Stack gap={24}>
              {qualification.healthData && <Alert tone="neg">{t('qualification.healthAlert')}</Alert>}
              <DefinitionList
                items={[
                  { label: t('newProject.verification.client'), value: `${clientTitle ?? ''}${client === NEW_CLIENT ? ` ${t('newProject.verification.newClient')}` : ''}` },
                  { label: t('newProject.verification.project'), value: `${title} (${preview.data.project ?? ''})` },
                  { label: t('newProject.verification.team'), value: team ?? '' },
                  { label: t('newProject.verification.criticality'), value: criticality ?? '' },
                  { label: t('newProject.verification.qualification'), value: obligations || t('newProject.summary.noObligation') },
                  { label: t('newProject.verification.applications'), value: chosenRepositories.map(r => `${r.split('/')[1]} (${(roles[r] ?? []).map(roleLabel).join(', ')})`).join(', ') },
                  { label: t('newProject.verification.environments'), value: t('newProject.verification.deployments', { environments: environments.map(envLabel).join(', '), n: preview.data.deployments }) },
                ]}
              />
              <Stack gap={12}>
                <Tabs
                  label={t('newProject.verification.files')}
                  value={tab}
                  onChange={setTab}
                  items={[{ id: 'catalogue', label: t('newProject.verification.catalogue') }, ...chosenRepositories.map(r => ({ id: r, label: r.split('/')[1] }))]}
                />
                {preview.data.files.filter(f => (tab === 'catalogue' ? f.repository === preview.data.files[0]?.repository : f.repository === tab)).map(f => (
                  // SPEC-006 plans a CodeBlock contribution; the DSM's read-only CodeField (2.3.0) shows code to copy.
                  <CodeField key={`${f.repository}/${f.path}`} label={`${f.repository} · ${f.path}`} language="yaml" readOnly lineNumbers value={f.content} rows={8} maxHeight={420} />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </WizardLayout>
      <Dialog
        open={quit}
        onClose={() => setQuit(false)}
        tone="danger"
        title={t('newProject.quitDialog.title')}
        description={t('newProject.quitDialog.body')}
        confirmLabel={t('newProject.quitDialog.confirm')}
        cancelLabel={t('newProject.quitDialog.cancel')}
        onConfirm={leave}
      />
    </>
  );
}

